
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "./supabaseClients";
import type { Order } from "./types";

export const useOrdersQuery = (shouldFetchData: boolean, isAuthReady: boolean) => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['orders'],
    queryFn: async (): Promise<Order[]> => {
      console.log('=== Orders Query Executing ===');
      console.log('Should fetch data:', shouldFetchData);
      console.log('Auth ready:', isAuthReady);
      console.log('Current time:', new Date().toISOString());
      
      // Check authentication status
      const staffAuth = localStorage.getItem('staff_sms_auth');
      const bypass = localStorage.getItem('location_bypass');
      console.log('📱 SMS Auth present:', !!staffAuth);
      console.log('🔑 Bypass present:', !!bypass);
      
      const client = getSupabaseClient();
      
      try {
        console.log('🔄 Fetching orders from Supabase...');
        
        // First fetch orders
        const { data: ordersData, error: ordersError, count } = await client
          .from('orders')
          .select('*', { count: 'exact' })
          .neq('status', 'archived') // Only show active and completed orders
          .order('created_at', { ascending: false });
        
        if (ordersError) {
          console.error('❌ Orders query error:', ordersError);
          
          // Don't throw errors for authentication issues - just return empty array
          if (ordersError.message.includes('Invalid API key') || ordersError.message.includes('authentication')) {
            console.log('⚠️ Authentication error - returning empty array');
            return [];
          }
          
          // For other errors, still return empty array but log them
          console.error('Database error:', ordersError.message);
          return [];
        }
        
        if (!ordersData) {
          console.log('⚠️ No data returned from query - returning empty array');
          return [];
        }
        
        // Fetch suppliers data to get supplier information
        const { data: suppliersData } = await client
          .from('suppliers')
          .select('name, supplier_code');
        
        console.log('🏪 Suppliers data fetched:', suppliersData);
        
        // Create lookup maps for both directions with clean data
        const supplierCodeToNameMap = new Map();
        const supplierNameToCodeMap = new Map();
        if (suppliersData) {
          suppliersData.forEach(supplier => {
            // Clean the data to avoid whitespace/case issues
            const cleanCode = supplier.supplier_code?.trim();
            const cleanName = supplier.name?.trim();
            
            if (cleanCode && cleanName) {
              // Map code to name (for when orders.supplier_name contains codes)
              supplierCodeToNameMap.set(cleanCode, cleanName);
              // Map name to code (for when orders.supplier_name contains names)
              supplierNameToCodeMap.set(cleanName, cleanCode);
            }
          });
        }
        
        console.log('🗺️ Supplier maps created:', {
          codeToName: Array.from(supplierCodeToNameMap.entries()),
          nameToCode: Array.from(supplierNameToCodeMap.entries())
        });
        
        console.log('Orders query result:', { 
          ordersData, 
          count,
          dataType: typeof ordersData,
          isArray: Array.isArray(ordersData)
        });
        
        console.log('📊 Orders query successful:', ordersData.length, 'orders found');
        
        // Map the data to our interface
        const mappedOrders = ordersData.map(order => {
          // Clean the supplier name from orders to match our clean maps
          const cleanSupplierName = order.supplier_name?.trim();
          
          // Determine if supplier_name is a code or actual name
          const isSupplierCode = supplierCodeToNameMap.has(cleanSupplierName);
          const supplierCode = isSupplierCode 
            ? cleanSupplierName
            : supplierNameToCodeMap.get(cleanSupplierName);
          const supplierName = isSupplierCode 
            ? supplierCodeToNameMap.get(cleanSupplierName)
            : cleanSupplierName;
          
          // Debug logging for each order
          console.log(`🔍 Mapping order ${order.id}:`, {
            originalSupplierName: order.supplier_name,
            cleanSupplierName,
            isSupplierCode,
            lookupResult: supplierCodeToNameMap.get(cleanSupplierName),
            finalSupplierName: supplierName || cleanSupplierName,
            finalSupplierCode: supplierCode || cleanSupplierName
          });
          
          return {
            id: order.id,
            supplierName: supplierName || cleanSupplierName || "Unknown Supplier", // Better fallback
            supplierCode: supplierCode || cleanSupplierName || "Unknown Code", // Better fallback
            deliveryDate: order.delivery_date,
            status: order.status as "active" | "completed" | "archived",
            productCount: order.product_count,
            hasCredit: order.has_credit,
            documents: order.documents || [],
            createdBy: order.created_by
          };
        });
        
        console.log('✅ Orders mapped successfully:', mappedOrders.length, 'orders');
        console.log('First few orders:', mappedOrders.slice(0, 3));
        return mappedOrders;
        
      } catch (error) {
        console.error('❌ Error in orders query function:', error);
        
        // Don't throw - just return empty array
        return [];
      }
    },
    enabled: shouldFetchData && isAuthReady,
    retry: false, // Disable retries to prevent endless requests
    staleTime: 30000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchInterval: false
  });
};
